var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { BaseModule, EventChannel } from '@syllepsis/adapter';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { AllSelection, NodeSelection } from 'prosemirror-state';
import { ToolbarLib } from '../..';
var IGNORE_CLOSE_ATTRIBUTE = 'data-syl-toolbar';
// needs special treatment of rect, otherwise the value of top and bottom are the same
var getPosRect = function (view, pos) {
    // CellSelection
    // @ts-ignore
    if (view.state.selection.$anchorCell) {
        return view.domAtPos(pos).node.getBoundingClientRect();
    }
    return view.coordsAtPos(pos);
};
/**
 * - positioning rules of InlineToolbar
 *  + vertical position is based on the selection area, horizon position is based on mouse position
 *  + display when there is text content, and display it at the top of the selection area first
 *  + When selecting across rows, it will be displayed above or below according to the position where the mouse is released
 *  + Drag to select. When the position where the mouse is released is too far from the selection, it will be displayed at the position where the mouse is released first, and keep the left and right positions
 *  + in other cases where there is a selection area but no `InlineToolbar`, move the mouse to appear, centered on the mouse position
 */
var ToolbarInlineLoader = /** @class */ (function (_super) {
    __extends(ToolbarInlineLoader, _super);
    function ToolbarInlineLoader(adapter, originOption) {
        var _this = _super.call(this, adapter, __assign({}, originOption)) || this;
        _this._visible = true;
        _this.mousedown = false;
        _this.preferDir = 'up'; // stay above or below the selection
        _this.preferLeft = 'auto'; // whether to recalculate the left position or fixed
        _this._isEnable = true;
        // stored click element because document.activeElement not works well in safari
        _this.lastClickElement = null;
        _this.enable = function () {
            _this.isEnable = true;
        };
        _this.disable = function () {
            _this.isEnable = false;
        };
        _this.show = function () {
            _this.visible = true;
        };
        _this.hide = function () {
            _this.visible = false;
        };
        _this.handleMouseUp = function (e) {
            // only handle the case where the mouse is pressed in the editor
            if (!_this.mousedown)
                return;
            _this.mousedown = false;
            _this.tracePos(e, true);
        };
        _this.handleMouseDown = function (e) {
            var button = e.button;
            if (button !== 2) {
                _this.mousedown = true;
                _this.hide();
            }
        };
        _this.tracePos = debounce(function (e, force) {
            if (!_this.isEnable || !_this.adapter.editable) {
                if (_this.visible)
                    _this.visible = false;
                return;
            }
            if (_this.mousedown)
                return;
            var view = _this.adapter.view;
            var state = view.state;
            var _a = state.selection, from = _a.from, to = _a.to, empty = _a.empty;
            if (empty ||
                !_this.adapter.getText({ index: from, length: to - from }).trim() ||
                _this.adapter.view.state.selection instanceof NodeSelection ||
                (_this.option.judgeShow && !_this.option.judgeShow(_this.adapter))) {
                _this.hide();
                return;
            }
            var visible = _this.visible;
            var dir = visible ? _this.preferDir : undefined;
            _this.show();
            _this.dom.style.visibility = 'hidden';
            // calculate the position based on the width and height and the position on the screen, remember to consider the position of the editor itself
            requestAnimationFrame(function () {
                var _a = state.selection, $head = _a.$head, $anchor = _a.$anchor;
                if (state.selection instanceof AllSelection) {
                    $anchor = state.doc.resolve(1);
                    var endPos = state.doc.nodeSize - 1;
                    while (endPos--) {
                        $head = state.doc.resolve(endPos);
                        if ($head.node().isTextblock)
                            break;
                    }
                    // display at the top when in table
                    // @ts-ignore
                }
                else if (state.selection.$anchorCell) {
                    var ranges = state.selection.ranges.sort(function (a, b) { return a.$from.pos - b.$from.pos; });
                    $anchor = ranges[0].$from;
                    $head = ranges[0].$to;
                }
                var _b = _this.threshold, thresholdTop = _b.top, thresholdLeft = _b.left, thresholdRight = _b.right, thresholdBottom = _b.bottom;
                var _c = getPosRect(view, $head.pos), headTop = _c.top, headLeft = _c.left, headBottom = _c.bottom;
                var _d = getPosRect(view, $anchor.pos), anchorBottom = _d.bottom, anchorTop = _d.top;
                var topPos = Math.min(headTop, anchorTop); // the top position of the selection area
                var bottomPos = Math.max(headBottom, anchorBottom); // the bottom position of the selection area
                var _e = _this.dom, offsetHeight = _e.offsetHeight, offsetWidth = _e.offsetWidth, offsetLeft = _e.offsetLeft;
                var domOffsetLeft = offsetWidth / 2;
                var _f = _this.adapter.root.getBoundingClientRect(), editorTop = _f.top, editorLeft = _f.left, editorRight = _f.right;
                var maxTop = window.innerHeight - offsetHeight - thresholdBottom;
                var minLeft = thresholdLeft;
                var maxLeft = editorRight - editorLeft - offsetWidth - thresholdRight; // The largest left, related to `thresholdRight`
                var defaultLeft = headLeft - domOffsetLeft - editorLeft; // align the left of the head position in the center
                var computedTop = topPos - offsetHeight - 8;
                _this.preferDir = 'up';
                // less than the `thresholdTop`, when selected across rows and not displayed, it will be judged according to the position of the head and anchor and displayed at the bottom
                if (computedTop < thresholdTop ||
                    (dir !== 'up' && Math.abs(anchorBottom - headBottom) > 12 && $head.pos > $anchor.pos)) {
                    computedTop = bottomPos + 8;
                    _this.preferDir = 'down';
                }
                // exceed the `maxTop`, displayed on the top
                if (computedTop > maxTop) {
                    computedTop = topPos - offsetHeight - 8;
                    _this.preferDir = 'up';
                }
                _this.dom.style.top = computedTop - editorTop + "px";
                // MouseEvent or directly invoke
                if (e instanceof Event || !visible) {
                    var target = void 0;
                    var pageX = void 0;
                    if (e instanceof Event) {
                        target = e.target;
                        pageX = e.pageX;
                    }
                    else {
                        target = _this.adapter.view.dom;
                        pageX = domOffsetLeft;
                    }
                    if (_this.adapter.view.dom.contains(target)) {
                        var mouseLeft = (visible ? offsetLeft + editorLeft : pageX - domOffsetLeft) - editorLeft; // mouse left position, if it is visible at this time, it should remain
                        var computedLeft = defaultLeft;
                        _this.preferLeft = 'auto';
                        // determine whether to use the cursor position or the mouse position(magic-number)
                        if (Math.abs(mouseLeft - defaultLeft) >= 20) {
                            computedLeft = mouseLeft;
                            _this.preferLeft = 'fixed';
                        }
                        if (computedLeft >= maxLeft) {
                            _this.preferLeft = 'auto';
                            computedLeft = maxLeft;
                        }
                        else if (computedLeft <= minLeft) {
                            _this.preferLeft = 'auto';
                            computedLeft = minLeft;
                        }
                        _this.dom.style.left = computedLeft + "px";
                    }
                }
                else if (force || _this.preferLeft === 'auto') {
                    _this.preferLeft = 'auto';
                    // force to adjust the position when refreshing, indenting, and undoing
                    _this.dom.style.left = Math.min(maxLeft, Math.max(minLeft, defaultLeft)) + "px";
                }
                _this.dom.style.visibility = '';
            });
        }, 20);
        // in other cases, check whether the toolbar needs to be displayed
        _this.checkShow = throttle(function (e) {
            if (_this.visible || !_this.adapter.isFocused) {
                return;
            }
            _this.tracePos(e);
        }, 20);
        _this.checkHide = debounce(function () {
            if (!_this.adapter.isFocused &&
                _this.lastClickElement &&
                !_this.dom.contains(_this.lastClickElement) &&
                !_this.lastClickElement.closest(IGNORE_CLOSE_ATTRIBUTE)) {
                _this.hide();
            }
        }, 300);
        _this.storedClick = function (e) { return (_this.lastClickElement = e.target); };
        _this.render = function () {
            _this.bridge.setProps({
                editor: _this.adapter,
                option: _this.option,
                visible: _this.visible,
                activeFormat: _this.adapter.getFormat(),
                toolbarLib: new ToolbarLib({ editor: _this.adapter, option: _this.option }),
            });
        };
        var option = __assign({}, originOption);
        _this.option = option;
        _this.dom = document.createElement('div');
        adapter.root.appendChild(_this.dom);
        _this.dom.style.position = 'absolute';
        _this.dom.style.userSelect = 'none';
        _this.dom.style.display = 'none';
        _this.dom.style.zIndex = "" + (option.zIndex || 100);
        _this.visible = false;
        _this.bridge = new option.RenderBridge(adapter, option.Component, _this.dom, 'toolbarInline');
        _this.bindEvent();
        _this.render();
        adapter.addCommand('toolbarInline', {
            show: function () { return _this.tracePos(undefined, true); },
            hide: _this.hide,
            getVisible: function () { return _this.visible; },
            getEnable: function () { return _this.isEnable && _this.adapter.editable; },
            enable: _this.enable,
            disable: _this.disable,
        });
        return _this;
    }
    Object.defineProperty(ToolbarInlineLoader.prototype, "isEnable", {
        get: function () {
            return this._isEnable;
        },
        set: function (val) {
            if (val === this._isEnable)
                return;
            if (!val)
                this.visible = false;
            this._isEnable = val;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ToolbarInlineLoader.prototype, "visible", {
        get: function () {
            return this._visible;
        },
        set: function (val) {
            var _this = this;
            if (!val && this._visible === val)
                return;
            this._visible = val;
            var newProps = { visible: val };
            if (val) {
                this.dom.style.display = 'block';
                newProps.activeFormat = this.updateFormat(false);
                // synchronize the displayed position when the state changes
                this.adapter.on(EventChannel.LocalEvent.ON_CHANGE, this.tracePos);
                this.adapter.view.dom.removeEventListener('mousemove', this.checkShow);
            }
            else {
                this.dom.style.display = 'none';
                this.adapter.off(EventChannel.LocalEvent.ON_CHANGE, this.tracePos);
                // in the case of undo, redo, paste, etc., determine whether it needs to be displayed
                this.adapter.view.dom.addEventListener('mousemove', this.checkShow);
            }
            // render the dom first, then update the props for achieve the animation
            requestAnimationFrame(function () { return _this.bridge.setProps(newProps); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ToolbarInlineLoader.prototype, "threshold", {
        get: function () {
            return __assign({ top: 0, left: 0, right: 0, bottom: 0 }, (this.option.threshold || {}));
        },
        enumerable: false,
        configurable: true
    });
    ToolbarInlineLoader.prototype.updateFormat = function (exec) {
        if (exec === void 0) { exec = true; }
        var activeFormat = this.adapter.getFormat();
        return exec ? this.bridge.setProps({ activeFormat: activeFormat }) : activeFormat;
    };
    ToolbarInlineLoader.prototype.bindEvent = function () {
        document.body.addEventListener('mouseup', this.handleMouseUp);
        document.body.addEventListener('click', this.storedClick);
        this.adapter.view.dom.addEventListener('mousedown', this.handleMouseDown);
        // do not trigger `mouseUp` when drop
        this.adapter.root.addEventListener('drop', this.handleMouseUp);
        this.adapter.on(EventChannel.LocalEvent.ON_BLUR, this.checkHide);
        this.adapter.on(EventChannel.LocalEvent.CONFIG_PLUGIN_CHANGE, this.render);
    };
    ToolbarInlineLoader.prototype.setProps = function (option) {
        this.option = __assign(__assign({}, this.option), option);
        this.render();
    };
    ToolbarInlineLoader.prototype.destructor = function () {
        document.body.removeEventListener('mouseup', this.handleMouseUp);
        document.body.removeEventListener('click', this.storedClick);
        this.adapter.view.dom.removeEventListener('mousedown', this.handleMouseDown);
        this.adapter.off(EventChannel.LocalEvent.ON_BLUR, this.checkHide);
        this.adapter.off(EventChannel.LocalEvent.CONFIG_PLUGIN_CHANGE, this.render);
        this.adapter.root.removeEventListener('drop', this.handleMouseUp);
        this.adapter.root.removeChild(this.dom);
        this.bridge.unmount();
    };
    return ToolbarInlineLoader;
}(BaseModule));
export { ToolbarInlineLoader };
//# sourceMappingURL=index.js.map